import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { clsx } from 'clsx'
import type { RegisterFlow } from '../hooks/useRegisterFlow'
import styles from './ProfileStep.module.css'

const profileSchema = z.object({
  nickname: z.string().max(20, 'Máximo 20 caracteres'),
  termsAccepted: z.boolean().refine(val => val === true, {
    message: 'Aceite os termos para continuar',
  }),
})

type ProfileForm = z.infer<typeof profileSchema>

type ProfileStepProps = {
  flow: RegisterFlow
}

export function ProfileStep({ flow }: ProfileStepProps) {
  // Generated once — stable across re-renders
  const defaultNickname = useMemo(
    () => `Jogador${Math.floor(1000 + Math.random() * 9000)}`,
    [],
  )

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: { nickname: defaultNickname, termsAccepted: false },
  })

  const termsAccepted = watch('termsAccepted')

  function handleSave(data: ProfileForm) {
    const nickname = data.nickname.trim() || defaultNickname
    void flow.saveProfile(nickname)
  }

  return (
    <div className={styles.step}>
      <div className={styles.progressDots} aria-label="Passo 3 de 3">
        <div className={clsx(styles.dot, styles.dotDone)} />
        <div className={clsx(styles.dot, styles.dotDone)} />
        <div className={clsx(styles.dot, styles.dotDone)} />
      </div>

      <h1 className={styles.heading}>Quase lá! 🎉</h1>
      <p className={styles.sub}>
        Como quer ser chamado <strong>na liga</strong>?
      </p>

      <form onSubmit={handleSubmit(handleSave)} noValidate>
        <div className={styles.fieldGroup}>
          <div className={styles.fieldLabelRow}>
            <label htmlFor="nickname">SEU APELIDO</label>
            <span className={styles.fieldOptional}>OPCIONAL</span>
          </div>
          <input
            id="nickname"
            className={clsx(styles.fieldInput, errors.nickname && styles.fieldInputError)}
            type="text"
            maxLength={20}
            autoComplete="username"
            autoFocus
            disabled={flow.isLoading || isSubmitting}
            {...register('nickname')}
          />
          {errors.nickname ? (
            <p className={styles.fieldError} role="alert">{errors.nickname.message}</p>
          ) : (
            <p className={styles.fieldHint}>Outros jogadores vão te ver assim na tabela.</p>
          )}
        </div>

        {/* Accessible custom checkbox */}
        <div className={styles.termsRow}>
          <label className={styles.termsLabel}>
            <input
              type="checkbox"
              className={styles.termsCheckboxNative}
              disabled={flow.isLoading || isSubmitting}
              {...register('termsAccepted')}
            />
            <span
              className={clsx(
                styles.termsCheckboxCustom,
                termsAccepted && styles.termsCheckboxChecked,
              )}
              aria-hidden="true"
            />
            <span className={styles.termsText}>
              Li e concordo com os{' '}
              <a href="/termos" target="_blank" rel="noopener noreferrer">
                Termos de Uso
              </a>
              {' '}e a{' '}
              <a href="/privacidade" target="_blank" rel="noopener noreferrer">
                Política de Privacidade
              </a>
              .
            </span>
          </label>
        </div>

        {errors.termsAccepted && (
          <p className={styles.formError} role="alert">{errors.termsAccepted.message}</p>
        )}
        {flow.error && (
          <p className={styles.formError} role="alert">{flow.error}</p>
        )}

        <button
          type="submit"
          className={styles.btnPrimary}
          disabled={!termsAccepted || flow.isLoading || isSubmitting}
        >
          {flow.isLoading || isSubmitting ? 'Entrando…' : 'ENTRAR NA LIGA'}
        </button>
      </form>
    </div>
  )
}
